import React, { useState, useEffect } from "react";
import useFetchInfo from "../../utils/useFetchInfo";
import { useParams, useNavigate } from "react-router-dom";
import apiServer from "../../constants/apiServer";
import { img_url_api } from "../../constants/apiImg";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import FormActionRow from "../../common/FormActionRow";
import "../../css/product.sass";
import DetailHeaderBar from "../../common/DetailHeaderBar";
import { Scrollbars } from "react-custom-scrollbars-2";
import GeneralInformation from "./GeneralInformation";
import { checkFormValidate } from "../../utils/commonFuncs";
import DetailedInformation from "./DetailedInformation";
import UploadMedia from "./UploadMedia";
import moment from "moment";
import { format } from "date-fns";
import { put } from "../../utils/api";
import PopupLoading from "../../common/PopupLoading";
import AdaAlert from "../../common/AdaAlert";
import Loading from "../../common/Loading";
import EmptyPage from "../../common/EmptyPage";

const productSteps = ["General Info", "Detailed Info", "Images"];

const UpdateProduct = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);

    const [generalInfoValues, setGeneralInfoValues] = useState({
        productEngName: "",
        productMyanName: "",
        productCode: "",
        chosenCategory: "",
        currency: 3,
        price: "",
        quantity: "",
        engDescription: "",
        myanDescription: "",
        creationDateRange: {
            startDate: new Date(),
            endDate: new Date(),
        },
        releasedDate: new Date(),
        isLimited: "1",
        isAvailable: "1",
        size: "",
        length: "",
        weight: "",
        warranty: "",
        mediaList: [],
    });

    const [generalInfoError, setGeneralInfoError] = useState({});
    const [creationDate, setCreationDate] = useState({});
    const [loading, setLoading] = useState(false);
    const [errorUpdate, setErrorUpdate] = useState("");
    const [deleteImgIdList, setDeleteImgIdList] = useState([]);

    const { data, isLoading, error } = useFetchInfo(
        `${apiServer.product.detail}/${productId}`,
    );

    useEffect(() => {
        if (data) {
            updateProductInfo(data);
        }
    }, [data]);

    const updateProductInfo = (productInfo) => {
        const historyFromDate = productInfo["history_from_date"];
        const historyToDate = productInfo["history_to_date"];
        const releaseDate = productInfo["release_date"];

        const formattedMedia = (productInfo["media"] || []).map((m) => ({
            ...m,
            type: m.media_type === "image" ? "image/" : "video/",

            file: m.s3_url,
            // Add src property for both images and videos
            src: img_url_api + m.s3_url,
        }));

        setGeneralInfoValues({
            productEngName: productInfo["name"]?.en || "",
            productMyanName: productInfo["name"]?.mm || "",
            productCode: productInfo["product_code"] || "",
            chosenCategory: {
                label: `${productInfo["category"]?.name?.eng} : ${productInfo["category"]?.name?.mm}`,
                value: productInfo["category_id"],
            },
            currency: productInfo["currency_id"] || 1,
            price: productInfo["price"] || "",
            quantity: productInfo["qty"] || "",
            engDescription: productInfo["description"]?.en || "",
            myanDescription: productInfo["description"]?.mm || "",
            creationDateRange: {
                startDate: moment(new Date(historyFromDate)),
                endDate: moment(new Date(historyToDate)),
            },
            releasedDate: new Date(releaseDate),
            isLimited: productInfo["isLimited"] ? "1" : "0",
            isAvailable: productInfo["isAvailable"] ? "1" : "0",
            size: productInfo["size"] || "",
            length: productInfo["length"] || "",
            weight: productInfo["weight"] || "",
            warranty: productInfo["warranty"] || "",
            mediaList: formattedMedia,
        });

        setCreationDate({
            startDate: historyFromDate,
            endDate: historyToDate,
        });
    };

    const handleNext = () => {
        if (activeStep === 0) {
            const infoValue = {
                productEngName: generalInfoValues.productEngName,
                productMyanName: generalInfoValues.productMyanName,
                productCode: generalInfoValues.productCode,
                chosenCategory: generalInfoValues.chosenCategory,
                currency: generalInfoValues.currency,
                price: generalInfoValues.price,
                quantity: generalInfoValues.quantity,
                engDescription: generalInfoValues.engDescription,
                myanDescription: generalInfoValues.myanDescription,
            };
            const checkGeneralInfo = checkFormValidate(infoValue);
            setGeneralInfoError(checkGeneralInfo);
            if (Object.keys(checkGeneralInfo).length === 0) {
                setActiveStep((prev) => prev + 1);
            }
        } else if (activeStep === 1) {
            setActiveStep((prev) => prev + 1);
        } else if (activeStep === 2) {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        const {
            productEngName,
            productMyanName,
            productCode,
            chosenCategory,
            currency,
            price,
            quantity,
            engDescription,
            myanDescription,
            releasedDate,
            isLimited,
            isAvailable,
            size,
            length,
            weight,
            warranty,
            mediaList,
        } = generalInfoValues;

        if (mediaList?.length > 0) {
            setLoading(true);
            const releasedAt = format(releasedDate, "yyyy-MM-dd");
            const imgList = new FormData();

            imgList.append(
                "name",
                JSON.stringify({ en: productEngName, mm: productMyanName }),
            );
            imgList.append(
                "description",
                JSON.stringify({ en: engDescription, mm: myanDescription }),
            );
            imgList.append("category_id", chosenCategory.value);
            imgList.append("product_code", productCode);
            imgList.append("price", price);
            imgList.append("currency_id", currency);
            imgList.append("qty", quantity);
            imgList.append(
                "isAvailable",
                isAvailable === "1" ? "true" : "false",
            );
            imgList.append("isLimited", isLimited === "1" ? "true" : "false");
            imgList.append("history_from_date", creationDate.startDate);
            imgList.append("history_to_date", creationDate.endDate);
            imgList.append("release_date", releasedAt);
            imgList.append("size", size);
            imgList.append("weight", weight);
            imgList.append("length", length);
            imgList.append("warranty", warranty);

            // ဖျက်မည့်ပုံဟောင်း ID များအား String ဖြင့်ပို့ခြင်း
            if (deleteImgIdList.length > 0) {
                imgList.append("delete_media_ids", deleteImgIdList.join(","));
            }

            // ပုံအသစ် (File objects) များကိုသာ Backend ဆီပို့ခြင်း
            for (let i = 0; i < mediaList.length; i++) {
                if (mediaList[i].file instanceof File) {
                    imgList.append("media_files", mediaList[i].file);
                }
            }

            try {
                const { data } = await put(
                    `${apiServer.product.update}/${productId}`,
                    imgList,
                );
                setLoading(false);
                if (data.success) {
                    navigate("/products", {
                        replace: true,
                        state: { status: "success", message: data.message },
                    });
                } else {
                    setErrorUpdate(data.message);
                }
            } catch (err) {
                setLoading(false);
                setErrorUpdate(
                    err.response?.data?.message || "Internal Server Error",
                );
            }
        } else {
            setErrorUpdate("Please upload product image");
        }
    };

    const handleUploadMedia = (info) =>
        setGeneralInfoValues((prev) => ({
            ...prev,
            mediaList: [...prev.mediaList, info],
        }));

    const handleRemoveMediaInfo = (info) => {
        setGeneralInfoValues((prev) => ({
            ...prev,
            mediaList: prev.mediaList.filter((el) => el !== info),
        }));
        // Database ထဲရှိပြီးသားပုံဖြစ်လျှင် ID သိမ်းထားရန်
        if (info.id) setDeleteImgIdList((prev) => [...prev, info.id]);
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        setGeneralInfoValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleChangeSelect = (name, val) =>
        setGeneralInfoValues((prev) => ({ ...prev, [name]: val }));
    const handleChangeDate = (val) =>
        setGeneralInfoValues((prev) => ({ ...prev, releasedDate: val }));
    const handleChangeDateRange = (s, e) => {
        setCreationDate({
            startDate: moment(s).format("YYYY-MM-DD"),
            endDate: moment(e).format("YYYY-MM-DD"),
        });
        setGeneralInfoValues((prev) => ({
            ...prev,
            releasedDate: new Date(moment(e).format("YYYY-MM-DD")),
            creationDateRange: { startDate: s, endDate: e },
        }));
    };

    if (isLoading) return <Loading text="Retrieving Product Information" />;
    if (error) return <EmptyPage text={error} />;
    if (loading) return <PopupLoading text="Updating product information" />;

    return (
        <div className="create-artist-page-section">
            <DetailHeaderBar
                navigateRoute={"/products"}
                name="Update Product"
            />
            <div className="create-product-page-section">
                <div className="create-product-page-stepper-container">
                    <Box sx={{ width: "100%" }}>
                        <Stepper activeStep={activeStep}>
                            {productSteps.map((label, idx) => (
                                <Step key={idx}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </div>
                <Scrollbars style={{ height: "calc(100vh - 200px)" }} autoHide>
                    {activeStep === 0 ? (
                        <GeneralInformation
                            states={generalInfoValues}
                            onChange={handleChangeInput}
                            errorStates={generalInfoError}
                            onChangeSelect={handleChangeSelect}
                        />
                    ) : activeStep === 1 ? (
                        <DetailedInformation
                            states={generalInfoValues}
                            onChange={handleChangeInput}
                            onChangeDateRange={handleChangeDateRange}
                            onChangeDate={handleChangeDate}
                            minReleaseDate={creationDate["endDate"]}
                        />
                    ) : (
                        <UploadMedia
                            mediaData={generalInfoValues}
                            onChange={handleUploadMedia}
                            onRemove={handleRemoveMediaInfo}
                        />
                    )}
                </Scrollbars>
                <FormActionRow
                    submitBtnName={
                        activeStep === productSteps.length - 1
                            ? "Update"
                            : "Next"
                    }
                    cancelBtnName="Back"
                    disableCancelBtn={activeStep === 0}
                    onSubmit={handleNext}
                    onCancel={() => setActiveStep((prev) => prev - 1)}
                />
            </div>
            {errorUpdate && <AdaAlert type="error" message={errorUpdate} />}
        </div>
    );
};

export default UpdateProduct;
